import { Component, Input, Output, EventEmitter, ChangeDetectorRef, inject } from '@angular/core';
import {
    ContentStrategy, StrategyPillar, ContentPlan, ContentPlanItem,
    ChannelConfig, ContentFeed, ContentTemplate, BrandConfig
} from '../../types';

type SettingsSection = 'brand' | 'strategy' | 'plan' | 'channels' | 'feeds' | 'templates' | 'integrations';

@Component({
    standalone: false,
    selector: 'mkt-settings-drawer',
    template: `
<div class="settings-page">

    <!-- Top header bar -->
    <div class="settings-page__header">
        <button class="settings-page__back" (click)="closeDrawer()">
            <i class="fa-solid fa-arrow-left"></i> Back to Dashboard
        </button>
        <h2 class="settings-page__title">
            <i class="fa-solid fa-gear"></i> Settings
        </h2>
    </div>

    <div class="settings-page__body">

        <!-- Sidebar navigation -->
        <nav class="settings-nav">
            <button class="settings-nav__item"
                    [class.settings-nav__item--active]="activeSection === 'brand'"
                    (click)="setSection('brand')">
                <i class="fa-solid fa-palette"></i> Brand
            </button>
            <button class="settings-nav__item"
                    [class.settings-nav__item--active]="activeSection === 'strategy'"
                    (click)="setSection('strategy')">
                <i class="fa-solid fa-compass"></i> Strategy
            </button>
            <button class="settings-nav__item"
                    [class.settings-nav__item--active]="activeSection === 'plan'"
                    (click)="setSection('plan')">
                <i class="fa-solid fa-list-ol"></i> Plan
            </button>
            <button class="settings-nav__item"
                    [class.settings-nav__item--active]="activeSection === 'channels'"
                    (click)="setSection('channels')">
                <i class="fa-solid fa-tower-broadcast"></i> Channels
            </button>
            <button class="settings-nav__item"
                    [class.settings-nav__item--active]="activeSection === 'feeds'"
                    (click)="setSection('feeds')">
                <i class="fa-solid fa-rss"></i> Feeds
            </button>
            <button class="settings-nav__item"
                    [class.settings-nav__item--active]="activeSection === 'templates'"
                    (click)="setSection('templates')">
                <i class="fa-solid fa-file-code"></i> Templates
            </button>
            <button class="settings-nav__item"
                    [class.settings-nav__item--active]="activeSection === 'integrations'"
                    (click)="setSection('integrations')">
                <i class="fa-solid fa-plug"></i> Integrations
            </button>
        </nav>

        <!-- Content area -->
        <div class="settings-content">

            <!-- ═══ BRAND ═══ -->
            <div *ngIf="activeSection === 'brand'" class="settings-section">
                <div class="settings-section__intro">
                    <h3 class="settings-section__heading"><i class="fa-solid fa-palette"></i> Brand Configuration</h3>
                    <p class="settings-section__desc">Configure your brand identity &mdash; voice, audience, and positioning</p>
                </div>
                <div class="settings-card">
                    <div *ngIf="!brandConfig" class="cmo-empty">No brand configuration. Create one in MJ Explorer.</div>
                    <div *ngIf="brandConfig">
                        <div class="cmo-form-row">
                            <label class="cmo-field__label">Brand Name</label>
                            <input [(ngModel)]="brandConfig.BrandName" class="cmo-input" />
                        </div>
                        <div class="cmo-form-row">
                            <label class="cmo-field__label">Voice & Tone</label>
                            <textarea [(ngModel)]="brandConfig.BrandVoiceAndTone" class="cmo-textarea" rows="3"
                                      placeholder="Describe how the brand sounds"></textarea>
                        </div>
                        <div class="cmo-form-row">
                            <label class="cmo-field__label">Target Audience</label>
                            <textarea [(ngModel)]="brandConfig.TargetAudience" class="cmo-textarea" rows="2"
                                      placeholder="Who are you writing for?"></textarea>
                        </div>
                        <div class="cmo-form-row">
                            <label class="cmo-field__label">Priorities</label>
                            <textarea [(ngModel)]="brandConfig.BrandPriorities" class="cmo-textarea" rows="2"
                                      placeholder="Current strategic priorities that should inform content"></textarea>
                        </div>
                        <div class="cmo-form-row">
                            <label class="cmo-field__label">Key Messaging Pillars</label>
                            <textarea [(ngModel)]="brandConfig.KeyMessagingPillars" class="cmo-textarea" rows="2"
                                      placeholder="Core themes all content should reinforce"></textarea>
                        </div>
                        <div class="cmo-form-row">
                            <label class="cmo-field__label">Dos & Don'ts</label>
                            <textarea [(ngModel)]="brandConfig.BrandDosAndDonts" class="cmo-textarea" rows="3"
                                      placeholder="DO: Use data to back claims. DON'T: Bash competitors by name."></textarea>
                        </div>
                        <div class="cmo-form-row">
                            <label class="cmo-field__label">Product Positioning</label>
                            <textarea [(ngModel)]="brandConfig.ProductPositioning" class="cmo-textarea" rows="2"
                                      placeholder="How should the product be positioned?"></textarea>
                        </div>
                        <div class="cmo-form-row">
                            <label class="cmo-field__label">Personality Traits</label>
                            <textarea [(ngModel)]="brandConfig.BrandPersonalityTraits" class="cmo-textarea" rows="2"
                                      placeholder="Traits that characterize the brand"></textarea>
                        </div>
                        <div class="cmo-form-row">
                            <label class="cmo-field__label">Min Brand Voice Score
                                <span style="font-size:0.7rem;color:var(--mj-text-muted)">(0-100, content below this is re-edited)</span>
                            </label>
                            <div style="display:flex;gap:8px;align-items:center">
                                <!-- TODO: remove $any() once CodeGen regenerates marketingBrandConfigurationEntity
                                     with MinBrandVoiceScore (added in V202604090001). -->
                                <input type="number" [(ngModel)]="$any(brandConfig).MinBrandVoiceScore"
                                       class="cmo-input" min="0" max="100" step="5" placeholder="70"
                                       style="max-width:80px" />
                            </div>
                        </div>
                        <div class="cmo-form-row">
                            <label class="cmo-field__label">Visual Identity Guidelines
                                <span style="font-size:0.7rem;color:var(--mj-text-muted)">(used for AI image generation)</span>
                            </label>
                            <textarea [(ngModel)]="brandConfig.VisualIdentityGuidelines" class="cmo-textarea" rows="5"
                                      placeholder="Flat editorial illustration. Muted warm palette..."></textarea>
                        </div>
                        <div class="settings-card__actions">
                            <button class="cmo-btn cmo-btn--sm" (click)="saveBrandConfig.emit()"
                                    [disabled]="saveStatus['brandConfig'] === 'saving'">
                                {{ getSaveLabel('brandConfig') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ═══ STRATEGY ═══ -->
            <div *ngIf="activeSection === 'strategy'" class="settings-section">
                <div class="settings-section__intro">
                    <h3 class="settings-section__heading"><i class="fa-solid fa-compass"></i> Content Strategy</h3>
                    <p class="settings-section__desc">Define your content strategy and strategic pillars</p>
                </div>
                <div class="settings-card">
                    <div *ngIf="!strategy" class="cmo-empty">No active strategy.</div>
                    <div *ngIf="strategy">
                        <div class="cmo-form-row">
                            <label class="cmo-field__label">Strategy Name</label>
                            <input [(ngModel)]="strategy.Name" class="cmo-input" />
                        </div>
                        <div class="cmo-form-row">
                            <label class="cmo-field__label">Objectives</label>
                            <textarea [(ngModel)]="strategy.Objectives" class="cmo-textarea" rows="3"></textarea>
                        </div>
                        <div class="settings-card__actions">
                            <button class="cmo-btn cmo-btn--sm" (click)="saveStrategy.emit()"
                                    [disabled]="saveStatus['strategy'] === 'saving'">
                                {{ getSaveLabel('strategy') }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="settings-card" *ngIf="strategy">
                    <div class="settings-card__title">Pillars</div>
                    <div *ngFor="let p of strategyPillars; let i = index" class="cmo-pillar-row">
                        <div class="cmo-form-row cmo-form-row--inline">
                            <input [(ngModel)]="p.Name" class="cmo-input" style="flex:1" placeholder="Pillar name" />
                            <input type="number" [(ngModel)]="p.Priority" class="cmo-input cmo-input--sm"
                                   style="width:50px" title="Priority (lower = higher)" />
                            <button class="cmo-btn cmo-btn--xs cmo-btn--danger"
                                    (click)="deletePillar.emit({pillar: p, index: i})" title="Delete pillar">
                                <i class="fa-solid fa-trash"></i>
                            </button>
                        </div>
                        <textarea [(ngModel)]="p.Description" class="cmo-textarea" rows="2"
                                  placeholder="Pillar description..."></textarea>
                        <div class="settings-card__actions">
                            <button class="cmo-btn cmo-btn--xs" (click)="savePillar.emit(p)"
                                    [disabled]="saveStatus['pillar-' + p.ID] === 'saving'">
                                {{ getSaveLabel('pillar-' + p.ID) }}
                            </button>
                        </div>
                    </div>
                    <button class="cmo-btn cmo-btn--sm cmo-btn--ghost" (click)="addPillar.emit()" *ngIf="strategy">
                        <i class="fa-solid fa-plus"></i> Add Pillar
                    </button>
                </div>
            </div>

            <!-- ═══ PLAN ═══ -->
            <div *ngIf="activeSection === 'plan'" class="settings-section">
                <div class="settings-section__intro">
                    <h3 class="settings-section__heading"><i class="fa-solid fa-list-ol"></i> Content Plan</h3>
                    <p class="settings-section__desc">Set up your content plan &mdash; what types and how often</p>
                </div>

                <div class="settings-card" *ngIf="contentPlan">
                    <div class="settings-card__title">Active Plan</div>
                    <div class="cmo-field"><strong>{{ contentPlan.Name }}</strong></div>
                    <div *ngIf="contentPlan.Description" class="cmo-field cmo-field--muted">{{ contentPlan.Description }}</div>
                </div>
                <div class="settings-card" *ngIf="!contentPlan">
                    <div class="cmo-empty">No active content plan configured.</div>
                </div>

                <div class="settings-card" *ngFor="let item of contentPlanItems">
                    <div class="settings-card__title">Plan Item: {{ item.Name }}</div>
                    <div class="cmo-form-grid">
                        <div class="cmo-form-row">
                            <label class="cmo-field__label">Name</label>
                            <input [(ngModel)]="item.Name" class="cmo-input" />
                        </div>
                        <div class="cmo-form-row cmo-form-row--inline">
                            <div>
                                <label class="cmo-field__label">Quantity</label>
                                <input type="number" [(ngModel)]="item.Quantity" class="cmo-input cmo-input--sm" style="width:60px" />
                            </div>
                            <div>
                                <label class="cmo-field__label">Frequency</label>
                                <select [(ngModel)]="item.Frequency" class="cmo-select">
                                    <option>Daily</option><option>Weekly</option><option>Biweekly</option><option>Monthly</option>
                                </select>
                            </div>
                            <div>
                                <label class="cmo-field__label">Trigger</label>
                                <select [(ngModel)]="item.TriggerType" class="cmo-select">
                                    <option>Schedule</option><option>ContentSource</option>
                                </select>
                            </div>
                            <div>
                                <label class="cmo-field__label">Blogs/Episode</label>
                                <input type="number" [(ngModel)]="item.BlogsPerEpisode" class="cmo-input cmo-input--sm" style="width:60px" />
                            </div>
                        </div>
                        <div class="cmo-form-row cmo-form-row--inline">
                            <label><input type="checkbox" [(ngModel)]="item.IncludeTranscriptBlog" /> Include transcript blog</label>
                            <label><input type="checkbox" [(ngModel)]="item.IsActive" /> Active</label>
                        </div>
                        <div class="cmo-form-row">
                            <label class="cmo-field__label">Notes (routing instructions for CMO)</label>
                            <textarea [(ngModel)]="item.Notes" class="cmo-textarea" rows="3"
                                      placeholder="e.g., For each new podcast episode: create 1 raw transcript blog + 2 AI derivatives..."></textarea>
                        </div>
                    </div>
                    <div class="settings-card__actions">
                        <button class="cmo-btn cmo-btn--sm" (click)="savePlanItem.emit(item)"
                                [disabled]="saveStatus['planItem-' + item.ID] === 'saving'">
                            {{ getSaveLabel('planItem-' + item.ID) }}
                        </button>
                    </div>
                </div>
                <div *ngIf="contentPlanItems.length === 0" class="settings-card">
                    <div class="cmo-empty">No plan items.</div>
                </div>
            </div>

            <!-- ═══ CHANNELS ═══ -->
            <div *ngIf="activeSection === 'channels'" class="settings-section">
                <div class="settings-section__intro">
                    <h3 class="settings-section__heading"><i class="fa-solid fa-tower-broadcast"></i> Channel Configuration</h3>
                    <p class="settings-section__desc">Configure approval rules and publishing settings per channel</p>
                </div>

                <!-- Default behavior note -->
                <div class="settings-card" style="background: var(--mj-bg-page); border-style: dashed;">
                    <div style="font-size: 0.8rem; color: var(--mj-text-secondary);">
                        <strong>Default:</strong> All content requires approval, no auto-publish. Add rules below to override per content type or platform.
                    </div>
                </div>

                <div class="settings-card">
                    <div *ngIf="channelConfigs.length === 0 && !showNewChannelForm" class="cmo-empty">No channel rules configured. Using defaults.</div>
                    <table *ngIf="channelConfigs.length > 0" class="cmo-table cmo-table--editable">
                        <thead>
                            <tr><th>Content Type</th><th>Platform</th><th>Approval</th><th>Auto-Publish</th><th>Max/Week</th><th></th></tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let ch of channelConfigs">
                                <td>{{ resolveContentTypeName(ch.ContentTypeID) }}</td>
                                <td>{{ ch.SocialPlatformID ? resolvePlatformName(ch.SocialPlatformID) : 'All Platforms' }}</td>
                                <td><label class="cmo-toggle-inline"><input type="checkbox" [(ngModel)]="ch.RequiresApproval" /></label></td>
                                <td><label class="cmo-toggle-inline"><input type="checkbox" [(ngModel)]="ch.AutoPublish" /></label></td>
                                <td><input type="number" [(ngModel)]="ch.MaxPerWeek" class="cmo-input cmo-input--sm" style="width:60px" /></td>
                                <td style="display:flex;gap:4px;">
                                    <button class="cmo-btn cmo-btn--xs" (click)="saveChannelConfig.emit(ch)"
                                            [disabled]="saveStatus['channel-' + ch.ID] === 'saving'">
                                        {{ getSaveLabel('channel-' + ch.ID) }}
                                    </button>
                                    <button class="cmo-btn cmo-btn--xs cmo-btn--danger" (click)="deleteChannelConfig.emit(ch.ID)" title="Delete rule">
                                        <i class="fa-solid fa-trash"></i>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <!-- New channel rule form -->
                    <div *ngIf="showNewChannelForm" style="margin-top:16px; padding-top:16px; border-top:1px solid var(--mj-border-default);">
                        <div class="cmo-form-row cmo-form-row--inline">
                            <div>
                                <label class="cmo-field__label">Content Type</label>
                                <select [(ngModel)]="newChannelContentTypeId" class="cmo-select">
                                    <option value="" disabled>Select...</option>
                                    <option *ngFor="let ct of contentTypes" [value]="ct.ID">{{ ct.Name }}</option>
                                </select>
                            </div>
                            <div>
                                <label class="cmo-field__label">Platform (optional)</label>
                                <select [(ngModel)]="newChannelPlatformId" class="cmo-select">
                                    <option value="">All Platforms</option>
                                    <option *ngFor="let sp of socialPlatforms" [value]="sp.ID">{{ sp.Name }}</option>
                                </select>
                            </div>
                            <div>
                                <label class="cmo-field__label">Approval</label>
                                <label class="cmo-toggle-inline"><input type="checkbox" [(ngModel)]="newChannelRequiresApproval" /></label>
                            </div>
                            <div>
                                <label class="cmo-field__label">Auto-Publish</label>
                                <label class="cmo-toggle-inline"><input type="checkbox" [(ngModel)]="newChannelAutoPublish" /></label>
                            </div>
                            <div>
                                <label class="cmo-field__label">Max/Week</label>
                                <input type="number" [(ngModel)]="newChannelMaxPerWeek" class="cmo-input cmo-input--sm" style="width:60px" />
                            </div>
                        </div>
                        <div style="margin-top:12px; display:flex; gap:8px;">
                            <button class="cmo-btn cmo-btn--sm cmo-btn--primary" [disabled]="!newChannelContentTypeId"
                                    (click)="emitAddChannelConfig()">
                                Add Rule
                            </button>
                            <button class="cmo-btn cmo-btn--sm" (click)="showNewChannelForm = false">Cancel</button>
                        </div>
                    </div>

                    <button *ngIf="!showNewChannelForm" class="cmo-btn cmo-btn--sm cmo-btn--ghost" style="margin-top:16px;"
                            (click)="showNewChannelForm = true">
                        <i class="fa-solid fa-plus"></i> New Channel Rule
                    </button>
                </div>
            </div>

            <!-- ═══ FEEDS ═══ -->
            <div *ngIf="activeSection === 'feeds'" class="settings-section">
                <div class="settings-section__intro">
                    <h3 class="settings-section__heading"><i class="fa-solid fa-rss"></i> Content Feeds</h3>
                    <p class="settings-section__desc">Manage RSS and content feeds the CMO monitors</p>
                </div>
                <div class="settings-card" *ngFor="let f of contentFeeds">
                    <div class="settings-card__title">
                        {{ f.Name || 'Content Feed' }}
                        <span *ngIf="!f.LastCheckedAt" class="cmo-badge">never checked</span>
                        <span *ngIf="f.LastCheckedAt && isFeedStale(f)" class="cmo-badge cmo-badge--warning">stale</span>
                    </div>
                    <div class="cmo-form-row">
                        <label class="cmo-field__label">Name</label>
                        <input [(ngModel)]="f.Name" class="cmo-input" />
                    </div>
                    <div class="cmo-form-row">
                        <label class="cmo-field__label">Feed URL</label>
                        <input [(ngModel)]="f.FeedURL" class="cmo-input" />
                    </div>
                    <div class="cmo-form-row cmo-form-row--inline">
                        <label><input type="checkbox" [(ngModel)]="f.IsActive" /> Active</label>
                        <span class="cmo-field--muted" style="font-size:0.75rem;">Last checked: {{ f.LastCheckedAt ? formatDateTime(f.LastCheckedAt) : 'never' }}</span>
                    </div>
                    <div class="settings-card__actions">
                        <button class="cmo-btn cmo-btn--sm" (click)="saveFeed.emit(f)"
                                [disabled]="saveStatus['feed-' + f.ID] === 'saving'">
                            {{ getSaveLabel('feed-' + f.ID) }}
                        </button>
                    </div>
                </div>
                <div *ngIf="contentFeeds.length === 0" class="settings-card">
                    <div class="cmo-empty">No content feeds configured.</div>
                </div>
            </div>

            <!-- ═══ TEMPLATES ═══ -->
            <div *ngIf="activeSection === 'templates'" class="settings-section">
                <div class="settings-section__intro">
                    <h3 class="settings-section__heading"><i class="fa-solid fa-file-code"></i> Content Templates</h3>
                    <p class="settings-section__desc">Nunjucks templates for content assembly</p>
                </div>
                <div class="settings-card" *ngFor="let t of contentTemplates">
                    <div class="settings-card__title">{{ t.Name }}</div>
                    <div class="cmo-form-row">
                        <label class="cmo-field__label">Name</label>
                        <input [(ngModel)]="t.Name" class="cmo-input" />
                    </div>
                    <div class="cmo-form-row">
                        <label class="cmo-field__label">Type</label>
                        <select [(ngModel)]="t.TemplateType" class="cmo-input">
                            <option *ngFor="let tt of templateTypes" [value]="tt">{{ tt }}</option>
                        </select>
                    </div>
                    <div class="cmo-form-row">
                        <label class="cmo-field__label">Template Body (Nunjucks)</label>
                        <textarea [(ngModel)]="t.TemplateBody" class="cmo-textarea cmo-textarea--code" rows="12"></textarea>
                    </div>
                    <div class="settings-card__actions">
                        <button class="cmo-btn cmo-btn--sm" (click)="saveTemplate.emit(t)"
                                [disabled]="saveStatus['template-' + t.ID] === 'saving'">
                            {{ getSaveLabel('template-' + t.ID) }}
                        </button>
                    </div>
                </div>
                <div *ngIf="contentTemplates.length === 0 && !showNewTemplateForm" class="settings-card">
                    <div class="cmo-empty">No templates configured.</div>
                </div>

                <!-- New template form -->
                <div *ngIf="showNewTemplateForm" class="settings-card">
                    <div class="settings-card__title">New Template</div>
                    <div class="cmo-form-row">
                        <label class="cmo-field__label">Name</label>
                        <input [(ngModel)]="newTemplateName" class="cmo-input" placeholder="e.g. Weekly Newsletter" />
                    </div>
                    <div class="cmo-form-row">
                        <label class="cmo-field__label">Content Type</label>
                        <select [(ngModel)]="newTemplateType" class="cmo-input">
                            <option *ngFor="let tt of templateTypes" [value]="tt">{{ tt }}</option>
                        </select>
                    </div>
                    <div class="settings-card__actions">
                        <button class="cmo-btn cmo-btn--sm cmo-btn--primary"
                                [disabled]="!newTemplateName.trim()"
                                (click)="createTemplate.emit({ name: newTemplateName.trim(), templateType: newTemplateType }); showNewTemplateForm = false; newTemplateName = '';">
                            Create
                        </button>
                        <button class="cmo-btn cmo-btn--sm" (click)="showNewTemplateForm = false; newTemplateName = '';">
                            Cancel
                        </button>
                    </div>
                </div>

                <button *ngIf="!showNewTemplateForm" class="cmo-btn cmo-btn--sm" style="margin-top: 12px;"
                        (click)="showNewTemplateForm = true">
                    <i class="fa-solid fa-plus"></i> New Template
                </button>
            </div>

            <!-- ═══ INTEGRATIONS ═══ -->
            <div *ngIf="activeSection === 'integrations'" class="settings-section">
                <div class="settings-section__intro">
                    <h3 class="settings-section__heading"><i class="fa-solid fa-plug"></i> Integrations</h3>
                    <p class="settings-section__desc">Connect external platforms for publishing, analytics, and trend research</p>
                </div>
                <div class="settings-card">
                    <div *ngIf="companyIntegrations.length === 0 && !showNewIntegrationForm" class="cmo-empty">No integrations connected.</div>
                    <table *ngIf="companyIntegrations.length > 0" class="cmo-table cmo-table--editable">
                        <thead>
                            <tr><th>Integration</th><th>Status</th><th>Credentials</th><th></th></tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let ci of companyIntegrations">
                                <td><strong>{{ ci.Integration || resolveIntegrationName(ci.IntegrationID) }}</strong></td>
                                <td>
                                    <span [style.color]="ci.IsActive ? 'var(--mj-color-success-400)' : 'var(--mj-text-disabled)'">
                                        {{ ci.IsActive ? 'Active' : 'Inactive' }}
                                    </span>
                                </td>
                                <td>
                                    <span *ngIf="ci.AccessToken" style="font-size:0.75rem;color:var(--mj-text-secondary)">Token set</span>
                                    <span *ngIf="ci.APIKey" style="font-size:0.75rem;color:var(--mj-text-secondary)">API Key set</span>
                                    <span *ngIf="!ci.AccessToken && !ci.APIKey" style="font-size:0.75rem;color:var(--mj-status-warning)">No credentials</span>
                                </td>
                                <td style="display:flex;gap:4px;">
                                    <button class="cmo-btn cmo-btn--xs" (click)="toggleCompanyIntegration.emit(ci.ID)">
                                        {{ ci.IsActive ? 'Disable' : 'Enable' }}
                                    </button>
                                    <button class="cmo-btn cmo-btn--xs cmo-btn--danger" (click)="deleteCompanyIntegration.emit(ci.ID)">
                                        <i class="fa-solid fa-trash"></i>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <!-- New integration form -->
                    <div *ngIf="showNewIntegrationForm" style="margin-top:16px; padding-top:16px; border-top:1px solid var(--mj-border-default);">
                        <div class="cmo-form-row">
                            <label class="cmo-field__label">Integration</label>
                            <select [(ngModel)]="newIntegrationId" class="cmo-select">
                                <option value="" disabled>Select integration...</option>
                                <option *ngFor="let integ of integrations" [value]="integ.ID">{{ integ.Name }} &mdash; {{ integ.Description }}</option>
                            </select>
                        </div>
                        <div class="cmo-form-row">
                            <label class="cmo-field__label">Access Token / PAT</label>
                            <input [(ngModel)]="newIntegrationToken" class="cmo-input" type="password" placeholder="Paste access token..." />
                        </div>
                        <div class="cmo-form-row">
                            <label class="cmo-field__label">API Key (if applicable)</label>
                            <input [(ngModel)]="newIntegrationApiKey" class="cmo-input" type="password" placeholder="Paste API key..." />
                        </div>
                        <div style="margin-top:12px; display:flex; gap:8px;">
                            <button class="cmo-btn cmo-btn--sm cmo-btn--primary"
                                    [disabled]="!newIntegrationId || (!newIntegrationToken && !newIntegrationApiKey)"
                                    (click)="emitAddIntegration()">
                                Connect
                            </button>
                            <button class="cmo-btn cmo-btn--sm" (click)="showNewIntegrationForm = false">Cancel</button>
                        </div>
                    </div>

                    <button *ngIf="!showNewIntegrationForm" class="cmo-btn cmo-btn--sm cmo-btn--ghost" style="margin-top:16px;"
                            (click)="showNewIntegrationForm = true">
                        <i class="fa-solid fa-plus"></i> Connect Integration
                    </button>
                </div>
            </div>

        </div>
    </div>
</div>
    `,
    styles: [`
        :host {
            display: flex;
            flex-direction: column;
            flex: 1;
            min-height: 0;
            font-family: 'Montserrat', 'Inter', -apple-system, sans-serif;
        }

        /* ─── Full-page container ─── */
        .settings-page {
            display: flex;
            flex-direction: column;
            height: 100%;
            min-height: 0;
            background: var(--mj-bg-page);
            color: var(--mj-text-primary);
        }

        /* ─── Top header bar ─── */
        .settings-page__header {
            display: flex;
            align-items: center;
            gap: 16px;
            padding: 14px 24px;
            background: var(--mj-bg-surface);
            border-bottom: 1px solid var(--mj-border-default);
            flex-shrink: 0;
        }
        .settings-page__back {
            display: inline-flex; align-items: center; gap: 6px;
            background: var(--mj-bg-surface); border: 1px solid var(--mj-border-default);
            padding: 6px 14px; font-size: 0.82rem; font-weight: 600;
            color: var(--mj-text-secondary); cursor: pointer; border-radius: 6px;
            transition: all 0.15s;
        }
        .settings-page__back:hover { color: var(--mj-brand-primary); border-color: var(--mj-brand-primary); }
        .settings-page__title {
            margin: 0; font-size: 1.1rem; font-weight: 700;
            color: var(--mj-text-primary); display: flex; align-items: center; gap: 8px;
        }
        .settings-page__title i { color: var(--mj-brand-primary); }

        /* ─── Body: sidebar + content ─── */
        .settings-page__body { display: flex; flex: 1; min-height: 0; overflow: hidden; }

        /* ─── Sidebar nav ─── */
        .settings-nav {
            width: 200px; flex-shrink: 0; background: var(--mj-bg-surface);
            border-right: 1px solid var(--mj-border-default); padding: 16px 0;
            display: flex; flex-direction: column; gap: 2px;
        }
        .settings-nav__item {
            display: flex; align-items: center; gap: 10px; padding: 10px 20px;
            font-size: 0.82rem; font-weight: 600; color: var(--mj-text-secondary);
            background: none; border: none; cursor: pointer; text-align: left;
            transition: all 0.15s; border-left: 3px solid transparent;
        }
        .settings-nav__item:hover { background: var(--mj-bg-page); color: var(--mj-text-primary); }
        .settings-nav__item--active {
            color: var(--mj-brand-primary); background: var(--mj-bg-page);
            border-left-color: var(--mj-brand-primary);
        }
        .settings-nav__item i { width: 18px; text-align: center; font-size: 0.85rem; }

        /* ─── Content area ─── */
        .settings-content {
            flex: 1; overflow-y: auto; padding: 28px 36px; min-width: 0;
        }
        .settings-section { max-width: 720px; }

        /* ─── Section intro ─── */
        .settings-section__intro { margin-bottom: 20px; }
        .settings-section__heading {
            margin: 0 0 6px 0; font-size: 1.15rem; font-weight: 700;
            color: var(--mj-text-primary); display: flex; align-items: center; gap: 8px;
        }
        .settings-section__heading i { color: var(--mj-brand-primary); font-size: 0.95rem; }
        .settings-section__desc {
            margin: 0; font-size: 0.85rem; color: var(--mj-text-secondary); line-height: 1.5;
        }

        /* ─── Settings card ─── */
        .settings-card {
            background: var(--mj-bg-surface); border: 1px solid var(--mj-border-default);
            border-radius: 10px; padding: 20px 24px; margin-bottom: 16px;
        }
        .settings-card__title {
            font-size: 0.88rem; font-weight: 700; color: var(--mj-text-primary);
            margin-bottom: 16px; display: flex; align-items: center; gap: 8px;
        }
        .settings-card__actions {
            margin-top: 16px; padding-top: 14px; border-top: 1px solid var(--mj-border-default);
        }

        /* ─── Complete form element styles (self-contained for encapsulation) ─── */
        .cmo-form-row { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px; }
        .cmo-form-row--inline { flex-direction: row; align-items: flex-end; gap: 12px; flex-wrap: wrap; }
        .cmo-form-grid { display: flex; flex-direction: column; gap: 4px; }

        .cmo-field__label {
            font-size: 0.72rem; font-weight: 700; text-transform: uppercase;
            letter-spacing: 0.4px; color: var(--mj-text-secondary); display: block; margin-bottom: 4px;
        }
        .cmo-field { font-size: 0.85rem; color: var(--mj-text-primary); line-height: 1.5; padding: 2px 0; }
        .cmo-field--muted { color: var(--mj-text-secondary); }
        .cmo-empty { text-align: center; padding: 16px; color: var(--mj-text-disabled); font-size: 0.85rem; }

        .cmo-input {
            font-size: 0.85rem; padding: 8px 12px; border-radius: 6px; width: 100%;
            border: 1px solid var(--mj-border-default); background: var(--mj-bg-page);
            color: var(--mj-text-primary); font-family: inherit; outline: none;
            box-sizing: border-box; transition: border-color 0.15s;
        }
        .cmo-input:focus { border-color: var(--mj-brand-primary); }
        .cmo-input--sm { padding: 6px 8px; font-size: 0.82rem; width: auto; }

        .cmo-textarea {
            width: 100%; font-size: 0.85rem; padding: 10px 12px; border-radius: 6px;
            border: 1px solid var(--mj-border-default); background: var(--mj-bg-page);
            color: var(--mj-text-primary); font-family: inherit; line-height: 1.6;
            resize: vertical; outline: none; box-sizing: border-box; transition: border-color 0.15s;
        }
        .cmo-textarea:focus { border-color: var(--mj-brand-primary); }
        .cmo-textarea::placeholder { color: var(--mj-text-disabled); }
        .cmo-textarea--code {
            font-family: 'SF Mono', 'Fira Code', 'Menlo', monospace; font-size: 0.8rem;
            line-height: 1.5; background: var(--mj-bg-surface-sunken); color: var(--mj-text-primary); border-color: var(--mj-border-strong);
        }
        .cmo-textarea--code:focus { border-color: var(--mj-color-indigo-500); }

        .cmo-select {
            font-size: 0.85rem; padding: 8px 12px; border-radius: 6px;
            border: 1px solid var(--mj-border-default); background: var(--mj-bg-page);
            color: var(--mj-text-primary); cursor: pointer; outline: none;
        }
        .cmo-select:focus { border-color: var(--mj-brand-primary); }

        .cmo-btn {
            display: inline-flex; align-items: center; gap: 6px;
            padding: 8px 16px; font-size: 0.82rem; font-weight: 600; border-radius: 6px;
            border: 1px solid var(--mj-border-default); background: var(--mj-bg-surface);
            color: var(--mj-text-primary); cursor: pointer; transition: all 0.15s;
        }
        .cmo-btn:hover:not(:disabled) { border-color: var(--mj-brand-primary); color: var(--mj-brand-primary); }
        .cmo-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .cmo-btn--sm { font-size: 0.78rem; padding: 6px 14px; }
        .cmo-btn--xs { font-size: 0.72rem; padding: 4px 10px; }
        .cmo-btn--primary { background: var(--mj-brand-primary); color: var(--mj-text-inverse); border-color: var(--mj-brand-primary); }
        .cmo-btn--primary:hover:not(:disabled) { filter: brightness(1.1); color: var(--mj-text-inverse); }
        .cmo-btn--danger { color: var(--mj-status-error); background: none; border-color: color-mix(in srgb, var(--mj-status-error) 30%, transparent); }
        .cmo-btn--danger:hover { background: var(--mj-status-error-bg); }
        .cmo-btn--ghost { color: var(--mj-text-secondary); background: none; border: 1px dashed var(--mj-border-default); }
        .cmo-btn--ghost:hover { color: var(--mj-text-primary); border-color: var(--mj-brand-primary); }

        .cmo-table { width: 100%; border-collapse: collapse; font-size: 0.82rem; }
        .cmo-table th {
            text-align: left; font-size: 0.72rem; font-weight: 700; text-transform: uppercase;
            letter-spacing: 0.4px; color: var(--mj-text-secondary); padding: 6px 10px 8px;
            border-bottom: 1px solid var(--mj-border-default);
        }
        .cmo-table td { padding: 8px 10px; color: var(--mj-text-primary); border-bottom: 1px solid var(--mj-border-default); }
        .cmo-table tr:last-child td { border-bottom: none; }
        .cmo-table--editable td { vertical-align: middle; }
        .cmo-toggle-inline input { cursor: pointer; width: 16px; height: 16px; accent-color: var(--mj-brand-primary); }

        .cmo-pillar-row {
            padding: 12px 0; border-bottom: 1px solid var(--mj-border-default);
            display: flex; flex-direction: column; gap: 8px;
        }
        .cmo-pillar-row:last-child { border-bottom: none; }

        .cmo-badge { font-size: 0.65rem; padding: 2px 8px; border-radius: 4px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; }
        .cmo-badge--warning { background: var(--mj-status-warning-bg); color: var(--mj-status-warning-text); }

        /* Checkbox labels */
        label { color: var(--mj-text-primary); font-size: 0.82rem; cursor: pointer; display: flex; align-items: center; gap: 6px; }
        label input[type="checkbox"] { accent-color: var(--mj-brand-primary); width: 16px; height: 16px; }

        /* ─── Responsive ─── */
        @media (max-width: 768px) {
            .settings-nav { width: 56px; padding: 12px 0; }
            .settings-nav__item { padding: 10px 0; justify-content: center; font-size: 0; border-left: none; }
            .settings-nav__item--active { border-left-color: transparent; }
            .settings-nav__item i { font-size: 1.1rem; }
            .settings-content { padding: 20px 16px; }
        }
    `]
})
export class SettingsDrawerComponent {
    private cdr = inject(ChangeDetectorRef);

    // ─── Inputs ───
    @Input() open: boolean = false;
    @Input() brandConfig: BrandConfig | null = null;
    @Input() strategy: ContentStrategy | null = null;
    @Input() strategyPillars: StrategyPillar[] = [];
    @Input() contentPlan: ContentPlan | null = null;
    @Input() contentPlanItems: ContentPlanItem[] = [];
    @Input() channelConfigs: ChannelConfig[] = [];
    @Input() contentFeeds: ContentFeed[] = [];
    @Input() contentTemplates: ContentTemplate[] = [];
    @Input() saveStatus: Record<string, 'idle' | 'saving' | 'saved' | 'error'> = {};
    @Input() contentTypes: { ID: string; Name: string }[] = [];
    @Input() socialPlatforms: { ID: string; Name: string }[] = [];
    @Input() integrations: { ID: string; Name: string; Description: string }[] = [];
    @Input() companyIntegrations: { ID: string; IntegrationID: string; Integration: string; IsActive: boolean; AccessToken: string | null; APIKey: string | null }[] = [];

    // ─── Outputs ───
    @Output() openChange = new EventEmitter<boolean>();
    @Output() saveBrandConfig = new EventEmitter<void>();
    @Output() saveStrategy = new EventEmitter<void>();
    @Output() savePillar = new EventEmitter<StrategyPillar>();
    @Output() addPillar = new EventEmitter<void>();
    @Output() deletePillar = new EventEmitter<{pillar: StrategyPillar, index: number}>();
    @Output() savePlanItem = new EventEmitter<ContentPlanItem>();
    @Output() saveChannelConfig = new EventEmitter<ChannelConfig>();
    @Output() addChannelConfig = new EventEmitter<{ contentTypeId: string; socialPlatformId: string | null; requiresApproval: boolean; autoPublish: boolean; maxPerWeek: number | null }>();
    @Output() deleteChannelConfig = new EventEmitter<string>();
    @Output() saveFeed = new EventEmitter<ContentFeed>();
    @Output() saveTemplate = new EventEmitter<ContentTemplate>();
    @Output() createTemplate = new EventEmitter<{ name: string; templateType: string }>();
    @Output() addCompanyIntegration = new EventEmitter<{ integrationId: string; accessToken?: string; apiKey?: string }>();
    @Output() toggleCompanyIntegration = new EventEmitter<string>();
    @Output() deleteCompanyIntegration = new EventEmitter<string>();

    // ─── Local State ───
    activeSection: SettingsSection = 'brand';
    showNewTemplateForm = false;
    newTemplateName = '';
    newTemplateType = 'Newsletter';
    templateTypes = ['Blog', 'Newsletter', 'Social', 'Podcast', 'Email Campaign', 'General'];

    // Channel rule form state
    showNewChannelForm = false;
    newChannelContentTypeId = '';
    newChannelPlatformId = '';
    newChannelRequiresApproval = true;
    newChannelAutoPublish = false;
    newChannelMaxPerWeek: number | null = null;

    // Integration form state
    showNewIntegrationForm = false;
    newIntegrationId = '';
    newIntegrationToken = '';
    newIntegrationApiKey = '';

    // ─── Methods ───

    setSection(s: SettingsSection): void {
        this.activeSection = s;
        this.cdr.detectChanges();
    }

    closeDrawer(): void {
        this.openChange.emit(false);
    }

    getSaveLabel(key: string): string {
        const s = this.saveStatus[key];
        if (s === 'saving') return 'Saving...';
        if (s === 'saved') return 'Saved';
        if (s === 'error') return 'Error \u2014 retry';
        return 'Save';
    }

    isFeedStale(feed: ContentFeed): boolean {
        if (!feed.LastCheckedAt) return false;
        const threeDaysAgo = new Date();
        threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
        return new Date(feed.LastCheckedAt) < threeDaysAgo;
    }

    formatDateTime(date: any): string {
        if (!date) return '\u2014';
        return new Date(date).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
        });
    }

    // ─── Name Resolution ───
    resolveContentTypeName(id: string): string {
        return this.contentTypes.find(ct => ct.ID === id)?.Name ?? id;
    }

    resolvePlatformName(id: string): string {
        return this.socialPlatforms.find(sp => sp.ID === id)?.Name ?? id;
    }

    resolveIntegrationName(id: string): string {
        return this.integrations.find(i => i.ID === id)?.Name ?? id;
    }

    // ─── Channel Rule Actions ───
    emitAddChannelConfig(): void {
        this.addChannelConfig.emit({
            contentTypeId: this.newChannelContentTypeId,
            socialPlatformId: this.newChannelPlatformId || null,
            requiresApproval: this.newChannelRequiresApproval,
            autoPublish: this.newChannelAutoPublish,
            maxPerWeek: this.newChannelMaxPerWeek,
        });
        this.showNewChannelForm = false;
        this.newChannelContentTypeId = '';
        this.newChannelPlatformId = '';
        this.newChannelRequiresApproval = true;
        this.newChannelAutoPublish = false;
        this.newChannelMaxPerWeek = null;
    }

    // ─── Integration Actions ───
    emitAddIntegration(): void {
        this.addCompanyIntegration.emit({
            integrationId: this.newIntegrationId,
            accessToken: this.newIntegrationToken || undefined,
            apiKey: this.newIntegrationApiKey || undefined,
        });
        this.showNewIntegrationForm = false;
        this.newIntegrationId = '';
        this.newIntegrationToken = '';
        this.newIntegrationApiKey = '';
    }
}
